<template>
    <div class="flex items-center justify-center py-4 px-2 md:min-h-screen bg-theme-bg2">
        <div class="w-full max-w-md">
            <div class="bg-theme-card border border-theme-border rounded-2xl overflow-hidden">
                <!-- Accent bar -->
                <div class="h-1 bg-gradient-to-r from-primary-600 to-secondary-500"></div>

                <div class="p-6">
                    <div class="flex justify-center items-center mb-4">
                        <img :src="isDark ? darkLogo : lightLogo" alt="logo" class="h-10 w-auto">
                    </div>
                    <div class="text-center mb-6">
                        <h2 class="text-2xl font-bold text-theme-text">Create your account</h2>
                        <p class="text-sm text-theme-muted mt-1">Enter your email and password to get started.</p>
                    </div>

            <form @submit.prevent="handleSignup" class="space-y-4">
                <!-- Step 1: Email + Password -->
                <div v-if="currentStep === 1">
                    <div class="relative mb-4">
                        <label for="email" class="block text-sm font-medium text-theme-text2 mb-1">Email address</label>
                        <input type="email" id="email" v-model="email" @input="debounceValidateEmail" placeholder="you@email.com"
                            :class="['block w-full border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 bg-theme-input text-theme-input-text text-sm', errors.email ? 'border-red-500 focus:ring-red-500' : 'border-theme-input-border focus:ring-primary-500']">
                        <p v-if="errors.email" class="text-red-500 text-xs mt-1">{{ errors.email }}</p>
                    </div>
                    <div class="relative mb-4">
                        <label for="password" class="block text-sm font-medium text-theme-text2 mb-1">Password</label>
                        <div class="relative">
                            <input :type="showPassword ? 'text' : 'password'" id="password" v-model="password" @input="validatePassword" placeholder="At least 8 characters"
                                :class="['block w-full border rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 bg-theme-input text-theme-input-text text-sm', errors.password ? 'border-red-500 focus:ring-red-500' : 'border-theme-input-border focus:ring-primary-500']">
                            <button type="button" @click="showPassword = !showPassword" class="absolute right-3 top-1/2 -translate-y-1/2 text-theme-muted">
                                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" class="text-sm"></i>
                            </button>
                        </div>
                        <p v-if="errors.password" class="text-red-500 text-xs mt-1">{{ errors.password }}</p>
                    </div>
                    <div class="relative mb-4">
                        <label for="confirmPassword" class="block text-sm font-medium text-theme-text2 mb-1">Confirm password</label>
                        <input :type="showPassword ? 'text' : 'password'" id="confirmPassword" v-model="confirmPassword" @input="validateConfirmPassword" placeholder="Repeat your password"
                            :class="['block w-full border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 bg-theme-input text-theme-input-text text-sm', errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : 'border-theme-input-border focus:ring-primary-500']">
                        <p v-if="errors.confirmPassword" class="text-red-500 text-xs mt-1">{{ errors.confirmPassword }}</p>
                    </div>
                    <button type="button" @click="goToTerms"
                        :disabled="!isStep1Valid"
                        :class="['w-full rounded-lg py-2.5 text-sm font-semibold transition', isStep1Valid ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-primary-300 text-white cursor-not-allowed']">
                        Continue
                    </button>
                </div>

                <!-- Step 2: Terms -->
                <div v-else-if="currentStep === 2">
                    <h3 class="text-base font-semibold text-theme-text mb-3">Terms & Conditions</h3>
                    <div class="h-44 overflow-y-auto border border-theme-border rounded-lg p-3 text-xs text-theme-text2 mb-4">
                        <div v-html="companyInfoStore.companyInfo?.terms_and_conditions || 'By creating an account you agree to our terms of service and privacy policy.'"></div>
                    </div>
                    <div class="flex items-center gap-2 mb-5">
                        <input type="checkbox" id="acceptTerms" v-model="acceptedTerms" class="w-4 h-4 accent-primary-600">
                        <label for="acceptTerms" class="text-sm text-theme-text2">I accept the terms and conditions</label>
                    </div>
                    <div class="flex gap-3">
                        <button type="button" @click="currentStep = 1" class="flex-1 rounded-lg py-2.5 text-sm font-semibold bg-theme-bg2 text-theme-text hover:bg-theme-bg3 transition">
                            Back
                        </button>
                        <button type="submit" :disabled="!acceptedTerms || loading"
                            :class="['flex-1 rounded-lg py-2.5 text-sm font-semibold transition flex items-center justify-center', acceptedTerms && !loading ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-primary-300 text-white cursor-not-allowed']">
                            <i v-if="loading" class="pi pi-spinner pi-spin mr-2 text-sm"></i>
                            {{ loading ? 'Creating account...' : 'Create Account' }}
                        </button>
                    </div>
                </div>
            </form>

            <p v-if="errorMessage" class="mt-4 text-red-500 text-sm text-center">{{ errorMessage }}</p>
                    <p class="mt-5 text-center text-sm text-theme-muted">
                        Already have an account?
                        <router-link to="/login" class="text-primary-600 font-semibold hover:underline">Sign in</router-link>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useCompanyInfoStore } from '@/store/company';
import { useTheme } from '@/composables/useTheme.js';
import useNotifications from '@/store/notification';
import darkLogo from '@/assets/images/dark_logo.png';
import lightLogo from '@/assets/images/logo.jpeg';

const { isDark } = useTheme();
const companyInfoStore = useCompanyInfoStore();
const { showNotification } = useNotifications();
const router = useRouter();
const api_base_url = import.meta.env.VITE_API_BASE_URL;

const currentStep = ref(1);
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const acceptedTerms = ref(false);
const showPassword = ref(false);
const loading = ref(false);
const errorMessage = ref('');
const errors = ref({});

const debounce = (fn, delay = 700) => {
    let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
};

const validateEmail = async () => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.value) { errors.value.email = 'Email is required'; return; }
    if (!pattern.test(email.value)) { errors.value.email = 'Enter a valid email'; return; }
    try {
        const { data } = await axios.get(`${api_base_url}/api/accounts/check-email`, { params: { email: email.value } });
        errors.value.email = data.exists ? 'An account with this email already exists.' : '';
    } catch { errors.value.email = ''; }
};
const debounceValidateEmail = debounce(validateEmail);

const validatePassword = () => {
    if (!password.value) { errors.value.password = 'Password is required'; return; }
    if (password.value.length < 8) { errors.value.password = 'Must be at least 8 characters'; return; }
    if (!/[A-Za-z]/.test(password.value) || !/\d/.test(password.value)) {
        errors.value.password = 'Must include letters and numbers'; return;
    }
    errors.value.password = '';
    if (confirmPassword.value) validateConfirmPassword();
};

const validateConfirmPassword = () => {
    errors.value.confirmPassword = confirmPassword.value === password.value ? '' : 'Passwords do not match';
};

const isStep1Valid = computed(() =>
    email.value && !errors.value.email &&
    password.value && !errors.value.password &&
    confirmPassword.value && !errors.value.confirmPassword
);

const goToTerms = () => {
    validatePassword();
    validateConfirmPassword();
    if (isStep1Valid.value) currentStep.value = 2;
};

const handleSignup = async () => {
    if (!acceptedTerms.value) return;
    loading.value = true;
    errorMessage.value = '';
    try {
        const response = await axios.post(`${api_base_url}/api/accounts/users/`, {
            email: email.value.toLowerCase().trim(),
            password: password.value,
        });
        if (response.status === 201) {
            Cookies.set('email', email.value, { expires: 7, sameSite: 'Lax' });
            showNotification('Account created!', 'Check your email for a verification code.', 'success');
            router.push('/verify');
        }
    } catch (err) {
        const data = err.response?.data;
        errorMessage.value = data?.error || data?.message || data?.detail || 'Sign up failed. Please try again.';
        showNotification('Error', errorMessage.value, 'error');
    } finally {
        loading.value = false;
    }
};
</script>
